import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, History } from 'lucide-react';
import { api } from '../../lib/api';
import Modal from '../ui/Modal';
import { AccountSelect, DealSelect, Money, Empty, fmtDate, usd, parseCents, centsToInput, today, inputCls, btnCls, btn2Cls, thCls, tdCls } from './shared';

// Every journal entry, newest first, with its lines. Chad can add a journal entry (capital,
// distributions, bills, corrections), edit one, or delete one. Tied-out months are locked.

const SOURCE_LABEL = { manual: 'Journal entry', relay_csv: 'Relay import', qbo_gl: 'QBO history' };

export default function RegisterTab({ me, accounts, deals }) {
  const [filters, setFilters] = useState({ from: `${new Date().getFullYear()}-01-01`, to: '', account_id: '', deal_id: '', q: '' });
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null); // entry | {} for new

  const load = useCallback(async () => {
    const qs = new URLSearchParams(Object.entries(filters).filter(([, v]) => v)).toString();
    try { setData(await api.get(`/books/entries?${qs}&limit=1000`)); setError(''); } catch (e) { setError(e.message); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const byId = useMemo(() => new Map(accounts.map((a) => [a.id, a])), [accounts]);
  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <input type="date" className={inputCls} value={filters.from} onChange={(e) => set('from')(e.target.value)} aria-label="From" />
        <input type="date" className={inputCls} value={filters.to} onChange={(e) => set('to')(e.target.value)} aria-label="To" />
        <AccountSelect accounts={accounts} value={filters.account_id} onChange={(v) => set('account_id')(v || '')} includeInactive placeholder="All accounts" />
        <DealSelect deals={deals} value={filters.deal_id} onChange={(v) => set('deal_id')(v || '')} />
        <input className={`${inputCls} min-w-0 flex-1 sm:max-w-xs`} placeholder="Search payee, memo" value={filters.q} onChange={(e) => set('q')(e.target.value)} />
        {me.can_edit && <button className={`${btnCls} ml-auto`} onClick={() => setOpen({})}><Plus size={14} /> Journal entry</button>}
      </div>
      {error && <p className="text-sm text-rose-600">{error}</p>}
      {!data ? <p className="text-sm text-[var(--text-secondary)]">Loading...</p> : data.entries.length === 0 ? <Empty>No entries for these filters.</Empty> : (
        <>
          <p className="text-xs text-[var(--text-secondary)]">{data.entries.length} of {data.total} entries</p>
          <div className="overflow-x-auto rounded-xl border border-[var(--border)] bg-white dark:bg-slate-900">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="bg-gray-50 dark:bg-slate-800"><tr>
                <th className={thCls}>Date</th><th className={thCls}>Description</th><th className={thCls}>Lines</th><th className={`${thCls} text-right`}>Amount</th>
              </tr></thead>
              <tbody>
                {data.entries.map((e) => {
                  const total = e.lines.reduce((s, l) => s + l.debit_cents, 0);
                  return (
                    <tr key={e.id} className="cursor-pointer border-t border-[var(--border)] hover:bg-gray-50 dark:hover:bg-slate-800" onClick={() => setOpen(e)}>
                      <td className={`${tdCls} whitespace-nowrap`}>{fmtDate(e.entry_date)}</td>
                      <td className={tdCls}>
                        <div className="text-[var(--text-primary)]">{e.counterparty || e.memo || SOURCE_LABEL[e.source]}</div>
                        <div className="text-xs text-[var(--text-secondary)]">
                          {SOURCE_LABEL[e.source]}{e.reconciled_period ? ` / tied ${e.reconciled_period}` : ''}{e.uncategorized ? ' / to categorize' : ''}
                        </div>
                      </td>
                      <td className={`${tdCls} text-xs text-[var(--text-secondary)]`}>
                        {e.lines.map((l) => <div key={l.id}>{l.debit_cents ? 'Dr' : 'Cr'} {byId.get(l.account_id)?.name}{l.deal_id ? ` (${deals.find((d) => d.id === l.deal_id)?.name || 'deal'})` : ''}</div>)}
                      </td>
                      <td className={`${tdCls} text-right`}><Money cents={total} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
      {open && <EntryModal entry={open.id ? open : null} me={me} accounts={accounts} deals={deals} onClose={() => setOpen(null)} onSaved={() => { setOpen(null); load(); }} />}
    </div>
  );
}

function blankLine() { return { account_id: null, debit: '', credit: '', deal_id: null, memo: '' }; }

function EntryModal({ entry, me, accounts, deals, onClose, onSaved }) {
  const isNew = !entry;
  const editable = me.can_edit;
  const [date, setDate] = useState(entry?.entry_date || today());
  const [memo, setMemo] = useState(entry?.memo || '');
  const [counterparty, setCounterparty] = useState(entry?.counterparty || '');
  const [lines, setLines] = useState(entry ? entry.lines.map((l) => ({ account_id: l.account_id, debit: l.debit_cents ? centsToInput(l.debit_cents) : '', credit: l.credit_cents ? centsToInput(l.credit_cents) : '', deal_id: l.deal_id, memo: l.memo || '' })) : [blankLine(), blankLine()]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState(null);

  const parsed = lines.map((l) => ({ ...l, d: l.debit === '' ? 0 : parseCents(l.debit), c: l.credit === '' ? 0 : parseCents(l.credit) }));
  const dr = parsed.reduce((s, l) => s + (l.d || 0), 0);
  const cr = parsed.reduce((s, l) => s + (l.c || 0), 0);
  const valid = parsed.length >= 2 && parsed.every((l) => l.account_id && l.d != null && l.c != null && (l.d > 0) !== (l.c > 0)) && dr === cr;
  const upd = (i, k, v) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));

  const save = async () => {
    setBusy(true); setError('');
    const body = { entry_date: date, memo: memo || null, counterparty: counterparty || null, lines: parsed.map((l) => ({ account_id: l.account_id, debit_cents: l.d, credit_cents: l.c, deal_id: l.deal_id || null, memo: l.memo || null })) };
    try {
      if (isNew) await api.post('/books/entries', body);
      else await api.patch(`/books/entries/${entry.id}`, { ...body, revision: entry.updated_at });
      onSaved();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm('Delete this entry? The audit log keeps a copy.')) return;
    setBusy(true);
    try { await fetchDelete(entry); onSaved(); } catch (e) { setError(e.message); setBusy(false); }
  };
  const loadHistory = async () => {
    try { setHistory((await api.get(`/books/audit?row_id=${entry.id}`)).audit); } catch (e) { setError(e.message); }
  };

  return (
    <Modal onClose={onClose} title={isNew ? 'New journal entry' : 'Entry'} maxWidth="max-w-4xl">
      <div className="space-y-3 text-sm">
        {entry?.source !== 'manual' && entry && (
          <p className="rounded-lg bg-gray-50 p-2 text-xs text-[var(--text-secondary)] dark:bg-slate-800">
            {SOURCE_LABEL[entry.source]}{entry.bank_payee ? `: ${entry.bank_payee}` : ''}{entry.bank_description ? ` / ${entry.bank_description}` : ''}. The bank amount is fixed; change the category side.
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          <input type="date" className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} disabled={!editable} aria-label="Date" />
          <input className={`${inputCls} min-w-0 flex-1`} placeholder="Payee or customer" value={counterparty} onChange={(e) => setCounterparty(e.target.value)} disabled={!editable} />
          <input className={`${inputCls} min-w-0 flex-1`} placeholder="Memo" value={memo} onChange={(e) => setMemo(e.target.value)} disabled={!editable} />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[680px]">
            <thead><tr><th className={thCls}>Account</th><th className={thCls}>Debit</th><th className={thCls}>Credit</th><th className={thCls}>Deal</th><th className={thCls}>Line memo</th><th /></tr></thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i}>
                  <td className="py-1 pr-1"><AccountSelect accounts={accounts} value={l.account_id} onChange={(v) => upd(i, 'account_id', v)} className="w-full" /></td>
                  <td className="py-1 pr-1"><input className={`${inputCls} w-28 text-right`} value={l.debit} onChange={(e) => upd(i, 'debit', e.target.value)} disabled={!editable} inputMode="decimal" /></td>
                  <td className="py-1 pr-1"><input className={`${inputCls} w-28 text-right`} value={l.credit} onChange={(e) => upd(i, 'credit', e.target.value)} disabled={!editable} inputMode="decimal" /></td>
                  <td className="py-1 pr-1"><DealSelect deals={deals} value={l.deal_id} onChange={(v) => upd(i, 'deal_id', v)} /></td>
                  <td className="py-1 pr-1"><input className={`${inputCls} w-full`} value={l.memo} onChange={(e) => upd(i, 'memo', e.target.value)} disabled={!editable} /></td>
                  <td>{editable && lines.length > 2 && <button className="text-[var(--text-secondary)]" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))} aria-label="Remove line"><Trash2 size={14} /></button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {editable && <button className={btn2Cls} onClick={() => setLines((ls) => [...ls, blankLine()])}><Plus size={14} /> Line</button>}
          <span className={`text-xs ${dr === cr ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-600'}`}>Debits {usd(dr)} / Credits {usd(cr)}{dr === cr ? ' / balanced' : ` / off by ${usd(dr - cr)}`}</span>
        </div>
        {error && <p className="text-rose-600">{error}</p>}
        <div className="flex flex-wrap items-center gap-2 border-t border-[var(--border)] pt-3">
          {editable && <button className={btnCls} disabled={!valid || busy} onClick={save}>{isNew ? 'Post entry' : 'Save changes'}</button>}
          {editable && !isNew && <button className={btn2Cls} disabled={busy} onClick={remove}><Trash2 size={14} /> Delete</button>}
          {!isNew && <button className={`${btn2Cls} ml-auto`} onClick={loadHistory}><History size={14} /> History</button>}
        </div>
        {history && (
          <ul className="max-h-48 overflow-y-auto text-xs text-[var(--text-secondary)]">
            {history.length === 0 && <li>No history.</li>}
            {history.map((h) => <li key={h.id}>{new Date(h.created_at).toLocaleString()} / {h.actor || 'system'} / {h.action}</li>)}
          </ul>
        )}
      </div>
    </Modal>
  );
}

async function fetchDelete(entry) {
  const res = await fetch(`/api/books/entries/${entry.id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${localStorage.getItem('mickey_token')}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ revision: entry.updated_at }),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `Delete failed (${res.status})`);
}
